import { useNavigate, useLocation } from "react-router-dom";
import { Button, Logo } from "../ui";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { bookAppointment, createPayAsYouGoIntent, saveOneTimePayment } from "../../shared/_services/common.services";
import { bookingRefusalMessage } from "../../shared/_helpers/meetingEntitlement";
import {
  PAYMENT_UNCONFIRMED_MESSAGE,
  isPaymentConfirmed,
  isUnauthorized,
  paymentRefusalMessage,
  rememberUnconfirmedPayment,
} from "../../shared/_helpers/paymentErrors";
import { useSnackbar } from 'notistack';
import { StripeCardForm } from "../plan/StripeCardForm";
import { PAY_AND_BOOK, PLEASE_WAIT } from "../plan/planCopy";
import { GENERIC_ERROR } from "../onboarding/onboardingCopy";
import { CHOOSE_PLAN_PATH } from "../../shared/_helpers/chatGate";

// /make-payment — Pooled inbox SP5 (spec § Removals): only the meeting
// add-on (`AddOn`) and pay-per-meeting (`PayAsYouGo`, owner item 14) are paid
// here now. The plan purchase moved to /plan/payment, a new card to
// /plan/card; the subscription, newCard and AddNewCard branches, the
// client-amount `create-stripe-intent` call and the reload hack are gone.
// Stripe is reached only through the plan screens' one adapter.

function getCurrentUserFromStorage() {
  return JSON.parse(localStorage.getItem("currentUser"));
}

function buildTransactionDetails(paymentIntent) {
  return {
    ...paymentIntent,
    transactionId: paymentIntent.id,
  };
}

// Returns `{ booked }` so the caller can tell a refusal from a success. It used to
// return nothing and `return` silently on any non-200, which is the whole defect
// here: the customer had already been charged and credited by that point, so a
// refused booking left them paid-up with no appointment and no message, and then
// the caller navigated them to the confirmation screen anyway.
export async function handleOneTimePaymentFlow({
  paymentIntent,
  duration,
  navigate,
  notify,
  isPayAsYouGo = false,
}) {
  const email = getCurrentUserFromStorage()?.user?.email;
  const pendingAppointment = JSON.parse(localStorage.getItem("pendingAppointment"));
  const transactionDetails = buildTransactionDetails(paymentIntent);

  const oneTimeRes = await saveOneTimePayment(email, transactionDetails, duration, isPayAsYouGo);
  // #88: saveOneTimePayment resolves to the AxiosError on a non-2xx (truthy),
  // so `if (!oneTimeRes)` never fired. A refused credit must not go on to book
  // an appointment the account has no credit for.
  if (!isPaymentConfirmed(oneTimeRes)) {
    if (isUnauthorized(oneTimeRes)) rememberUnconfirmedPayment();
    notify?.(paymentRefusalMessage(oneTimeRes));
    return { booked: false };
  }

  const appointmentRes = await bookAppointment(pendingAppointment);

  // Backend ARCH-036 made `add-appointment` enforce the balance server-side: 403
  // with `{ message, meetings_required, meetings_available }` when it cannot cover
  // the booking, 409 when a concurrent update to the balance collides. The 409 is
  // plausible precisely here, because this booking races the credit the payment
  // just wrote. `bookAppointment` resolves rather than throws, so both arrive as an
  // axios error whose status is on `.response` — see `bookingRefusalMessage`.
  //
  // `pendingAppointment` is deliberately left in localStorage on a refusal: it is
  // what a retry replays, and the credit is already on the account, so rebooking
  // spends it rather than charging again.
  const unauthorized = isUnauthorized(appointmentRes);
  const refusal = unauthorized ? PAYMENT_UNCONFIRMED_MESSAGE : bookingRefusalMessage(appointmentRes);
  if (refusal) {
    if (unauthorized) rememberUnconfirmedPayment();
    notify?.(refusal);
    return { booked: false };
  }

  window.localStorage.setItem("bookedAppointment", JSON.stringify(appointmentRes?.data));
  window.localStorage.removeItem("pendingAppointment");
  navigate(isPayAsYouGo ? "/meetings" : "/confirm-payment");
  return { booked: true };
}

const MeetingPaymentForm = ({ type, duration, field, ready, loadError, complete, confirm }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPaymentLoader, setShowPaymentLoader] = useState(false);
  // One payment at a time: a second submit before the first settles (Enter
  // twice, a double click) must not confirm the PaymentIntent again.
  const inFlight = useRef(false);

  // A refusal message has to outlive the flow that produced it, because the
  // `navigate('/confirm-payment')` below would otherwise replace it with a
  // confirmation screen for an appointment that was never booked.
  const showError = (message) =>
    enqueueSnackbar(message, {
      variant: 'error',
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!ready || !complete || inFlight.current) return;
    inFlight.current = true;
    setShowPaymentLoader(true);
    try {
      const result = await confirm('payment');
      if (!result.ok) {
        // Stripe's own message (a decline, a failed 3-D Secure check).
        if (result.error.message) showError(result.error.message);
        inFlight.current = false;
        setShowPaymentLoader(false);
        return;
      }
      if (result.intent?.status !== 'succeeded') throw new Error("Payment Error!");
      await handleOneTimePaymentFlow({
        paymentIntent: result.intent,
        duration,
        navigate,
        notify: showError,
        isPayAsYouGo: type === 'PayAsYouGo',
      });
    } catch (err) {
      console.warn(err);
      showError(GENERIC_ERROR);
    }
    // handleOneTimePaymentFlow navigates on success; a failure stays here with
    // its message.
    inFlight.current = false;
    setShowPaymentLoader(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {field}
      {loadError && (
        <p role="alert" className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-danger">
          {loadError}
        </p>
      )}
      <div className="tw-mt-8 tw-text-center">
        <Button
          type="submit"
          className="tw-w-full"
          disabled={!ready || !complete || showPaymentLoader}
        >
          {showPaymentLoader ? PLEASE_WAIT : PAY_AND_BOOK}
        </Button>
      </div>
    </form>
  );
};

const MakePayment = () => {
  const [clientSecret, setClientSecret] = useState(null);
  const [duration, setDuration] = useState();
  const { cardContentType } = useSelector((state) => state?.stripeDetails?.data);
  const navigate = useNavigate();
  const location = useLocation();
  const addonMeeting = location?.state?.from === "addtionalMeeting" ? location.state : "";

  useEffect(() => {
    if (cardContentType === "PayAsYouGo") {
      // No `amount`: the server prices the meeting from `duration` against the
      // platform singleton (SEC-048).
      (async () => {
        const response = await createPayAsYouGoIntent(
          addonMeeting?.email,
          addonMeeting?.duration
        );
        // createPayAsYouGoIntent resolves with the axios error: 402 is the chat
        // gate (no plan).
        if (response?.response?.status === 402) {
          navigate(CHOOSE_PLAN_PATH, { replace: true });
          return;
        }
        setClientSecret(response?.data?.clientSecret);
        setDuration(addonMeeting.duration);
      })();
    } else if (cardContentType === 'AddOn') {
      setClientSecret(addonMeeting?.clientSecret);
      setDuration(addonMeeting?.duration);
    }
  }, [cardContentType, addonMeeting, navigate]);

  const payable = cardContentType === 'AddOn' || cardContentType === 'PayAsYouGo';

  return (
    <div className="stars-dark tw-min-h-screen tw-bg-bg tw-font-sans tw-text-text">
      <main className="tw-mx-auto tw-w-full tw-max-w-lg tw-px-4 tw-pb-12 tw-pt-6 md:tw-pt-8">
        <Logo alt="" className="tw-w-36" />
        <div className="tw-mt-12">
          {clientSecret && payable && (
            <StripeCardForm clientSecret={clientSecret}>
              {(form) => (
                <MeetingPaymentForm type={cardContentType} duration={duration} {...form} />
              )}
            </StripeCardForm>
          )}
        </div>
      </main>
    </div>
  );
};

export default MakePayment;
